import { useMutation } from '@apollo/client';

import { useLingui } from '@lingui/react';
import { ActivityType } from '@zeus/index';

import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { GQL_CREATE_TRACKING } from '~/gql/tracking/tracking';
import { GQL_UPDATE_TRACKING_THEMATIQUE } from '~/gql/tracking-thematique/tracking-thematique';
import useInactivityDetect from '~/helpers/hooks/activity-detect';
import useSoundAudio from '~/helpers/hooks/soundAudio';
import { useTimer } from '~/helpers/hooks/timer';
import useConnectionStore from '~/helpers/store/connection/connection';
import {
  changeActivityOrder,
  parseTextToJSX,
} from '~/pages/components/game/activities-forms/utils';

import { SDivParsedItem } from '~/pages/components/styles/styles';
import Button from '~/pages/components/ui/button';
import { ClickImageObjectItem } from '~/pages/components/ui/click-image-object-item';
import { FeedbackItem } from '~/pages/components/ui/feedback-item';
import { QuestionItem } from '~/pages/components/ui/question-item';
import { TActivity } from '~/utils/airtable/types/types';
import { renderFullPath } from '~/utils/common/utils';

type IProps = {
  currentActivity: TActivity;
  nextActivity: () => void;
};

function ClickObject({ currentActivity, nextActivity }: IProps) {
  const { i18n } = useLingui();
  const { playSound } = useSoundAudio();

  const { currentTrackingThematiqueId, setCurrentTrackings, currentTrackings } =
    useConnectionStore();

  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const { time, start, stop, clear } = useTimer();

  const [updateTrackingThematique] = useMutation(
    GQL_UPDATE_TRACKING_THEMATIQUE
  );
  const [createTracking] = useMutation(GQL_CREATE_TRACKING);

  const isActive = useInactivityDetect({ timeout: 5000 });

  useEffect(() => {
    if (!isActive) {
      stop();
    } else {
      start();
    }
  }, [isActive]);

  useEffect(() => {
    if (submitted) {
      if (isResponseCorrect()) playSound('feedbackGood');
      else playSound('feedbackWrong');
    }
  }, [submitted]);

  useEffect(() => {
    setSelectedAnswers([]);
    setSubmitted(false);
    start();
    if (currentTrackingThematiqueId) {
      setCurrentTrackings(
        changeActivityOrder(
          currentTrackingThematiqueId,
          currentActivity.order,
          currentTrackings
        )
      );
    }

    setCorrectAnswers(
      currentActivity.answers
        ?.filter((el) => el.isCorrect)
        .map((a) => a.text) ?? []
    );
  }, [currentActivity]);

  function handleClickValid() {
    setSubmitted(true);
    stop();
  }

  async function handleNext() {
    try {
      if (currentTrackingThematiqueId) {
        await updateTrackingThematique({
          variables: { id: currentTrackingThematiqueId, time: time },
        });

        await createTracking({
          variables: {
            trackingThematiqueId: currentTrackingThematiqueId,
            activityOrder: currentActivity.order,
            activityType: currentActivity.type as ActivityType,
            answers: selectedAnswers,
            isCorrect: isResponseCorrect(),
            time: time === 0 ? 1 : time,
          },
        });
      }
    } finally {
      clear();
      playSound('click');
      nextActivity();
    }
  }

  function isResponseCorrect() {
    let isCorrect = true;
    const _correctAnswers = [...correctAnswers];

    for (const answer of selectedAnswers) {
      if (!_correctAnswers.includes(answer)) {
        isCorrect = false;
      }
    }
    return isCorrect && selectedAnswers.length === _correctAnswers.length;
  }

  return (
    <Container $background={renderFullPath(currentActivity.background)}>
      <SDivBlur $withBackground={submitted}>
        <SDivContent>
          <SDivQuestion>
            {submitted ? (
              <>
                <FeedbackItem $margin="0 0 5px 0">
                  {isResponseCorrect() ? (
                    <SDivParsedItem>
                      {parseTextToJSX(currentActivity.feedbackCorrect ?? '')}
                    </SDivParsedItem>
                  ) : (
                    <SDivParsedItem>
                      {parseTextToJSX(currentActivity.feedbackIncorrect ?? '')}
                    </SDivParsedItem>
                  )}
                </FeedbackItem>
              </>
            ) : (
              <>
                <QuestionItem
                  text={currentActivity.text}
                  characterImg={renderFullPath(currentActivity.characterImg)}
                  $margin="0 0 5px 0"
                  advice={i18n._('activities.advice.click.object')}
                />
              </>
            )}
          </SDivQuestion>

          <SDivClickImage>
            <ClickImageObjectItem
              currentActivity={currentActivity}
              selectedAnswers={selectedAnswers}
              onChangeSelectedAnswers={setSelectedAnswers}
              correctAnswers={correctAnswers}
              $submitted={submitted}
            />
          </SDivClickImage>
        </SDivContent>
        <SDivBtn>
          <Button
            color={submitted ? 'secondary' : 'primary'}
            disabled={!submitted && selectedAnswers.length === 0}
            onClick={submitted ? handleNext : handleClickValid}
          >
            {submitted ? i18n._('actions.next') : i18n._('actions.valid')}
          </Button>
        </SDivBtn>
      </SDivBlur>
    </Container>
  );
}

export default ClickObject;

const Container = styled.div<{ $background?: string }>`
  align-items: center;
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
`;

const SDivContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex: 0 0 85%;
  width: 100%;
`;

const SDivBtn = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  flex: 0 0 15%;
  width: 100%;
`;

const SDivQuestion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 0 0 30%;
  width: 100%;
`;

const SDivClickImage = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 70%;
  flex-wrap: wrap;
  justify-content: center;
  padding: 30px 0;
  width: 100%;
`;

const SDivBlur = styled.div<{ $withBackground?: boolean }>`
  display: flex;
  background-color: ${({ $withBackground }) =>
    $withBackground ? '#56afdc80' : 'transparent'};
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
